import { FC, useEffect, useState } from 'react'

import cn from './index.module.css'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IUserNote } from './utils/types'
import Preloader from '@UI/Preloader'
import NoteCard from './components/NoteCard/NoteCard'
import AddNoteCard from './components/AddNoteCard/AddNoteCard'
import {
  createUserNote,
  getUserNotes,
  removeUserNote,
  updateUserNote,
} from './utils/api'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import { Modal as AModal } from 'antd'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'

interface INotesList {
  userId: number
}

const test = [
  {
    id: 1,
    title: 'Идея для проекта',
    text: 'Создать приложение для ведения заметок с синхронизацией между устройствами.',
  },
  {
    id: 2,
    title: 'Покупки',
    text: 'Молоко, хлеб, яйца, кофе, фрукты. Купить билеты, забронировать отель, составить маршрут. Создать приложение для ведения заметок с синхронизацией между устройствами.Создать приложение для ведения заметок с синхронизацией между устройствами.Создать приложение для ведения заметок с синхронизацией между устройствами. «Успех — это сумма маленьких усилий, повторяющихся день за днём.» Закончить презентацию, сходить к стоматологу, оплатить счета.',
  },
]

function NoteModalContent({
  title,
  body,
  onChange,
}: {
  title: string
  body: string
  onChange: (title: string, body: string) => void
}) {
  const [newTitle, setNewTitle] = useState(title)
  const [newBody, setNewBody] = useState(body)

  return (
    <div className={cn.modalContentWrapper}>
      <TextField
        className={cn.field}
        label="Заголовок"
        value={newTitle}
        placeholder="заголовок"
        onChange={(value) => {
          setNewTitle(value)
          onChange(value, newBody)
        }}
      />
      <TextAreaField
        label="Заметка"
        className={cn.field}
        placeholder="текст"
        value={newBody}
        onChange={(value) => {
          setNewBody(value?.target?.value)
          onChange(newTitle, value?.target?.value)
        }}
        autoSize={{ minRows: 3, maxRows: 6 }}
      />
    </div>
  )
}

const NotesList: FC<INotesList> = ({ userId }) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState<boolean>(true)
  const [notes, setNotes] = useState<IUserNote[]>([])

  const fetchNotes = async () => {
    setLoading(true)
    const notes = await getUserNotes(userId)
    setNotes(notes)
    setLoading(false)
  }

  useEffect(() => {
    fetchNotes()
  }, [userId])

  const onUpdateNote = async (note?: IUserNote) => {
    const modalRes: { resolve: boolean; title?: string; body?: string } =
      await new Promise((resolve) => {
        let title = note?.title ?? ''
        let body = note?.text ?? ''

        AModal.confirm({
          title: note?.id ? 'Обновить заметку' : 'Создать заметку',
          content: (
            <NoteModalContent
              title={title}
              body={body}
              onChange={(newTitle, newBody) => {
                title = newTitle
                body = newBody
              }}
            />
          ),
          icon: null,
          centered: true,
          maskClosable: true,
          className: jclasses.modal,
          okText: 'Сохранить',
          cancelText: 'Отменить',
          onOk: () => {
            if (!title || !body) {
              addAlert({
                title: 'Нужно заполнить все поля',
                type: 'error',
              })
              return Promise.reject()
            }
            resolve({ resolve: true, title: title, body: body })
          },
          onCancel: () => resolve({ resolve: false }),
        })
      })

    if (modalRes?.resolve && modalRes.title && modalRes.body) {
      if (note?.id) {
        const res = await updateUserNote({
          id: note.id,
          title: modalRes.title,
          text: modalRes.body,
        })

        if (!!res) {
          addAlert({ title: 'Успешно', text: 'Заметка обновлена' })
          setNotes((prev) =>
            prev.map((item) => {
              if (item.id === note?.id) {
                return res
              } else {
                return item
              }
            }),
          )
        } else {
          addAlert({
            title: 'Ошибка',
            text: 'Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
        }
      } else {
        const res = await createUserNote(userId, {
          id: 0,
          title: modalRes.title,
          text: modalRes.body,
        })

        if (!!res) {
          addAlert({ title: 'Успешно', text: 'Заметка сохранена' })
          setNotes((prev) => [...prev, res])
        } else {
          addAlert({
            title: 'Ошибка',
            text: 'Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
        }
      }
    }
  }

  const onRemove = async (note: IUserNote) => {
    const isApproved = await new Promise((resolve) => {
      AModal.confirm({
        title: 'Удалить',
        content: 'Вы уверены, что хотите удалить эту заметку?',
        icon: null,
        centered: true,
        maskClosable: true,
        className: jclasses.modal,
        okText: 'Подтвердить',
        cancelText: 'Отменить',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })

    if (isApproved) {
      const res = await removeUserNote(note.id)

      if (res) {
        addAlert({ title: 'Успешно', text: 'Заметка удалена из списка' })
        setNotes((prev) => prev.filter((item) => item.id !== note.id))
      } else {
        addAlert({
          title: 'Ошибка',
          text: 'Попробуйте ещё раз или обратитесь к администратору',
          type: 'error',
        })
      }
    }
  }

  return (
    <div className={cn.wrapper}>
      {loading ? (
        <div className={cn.stubWrapper}>
          <Preloader />
        </div>
      ) : !notes?.length ? (
        <div className={cn.stubWrapper}>
          <AddNoteCard onClick={onUpdateNote} />
        </div>
      ) : (
        <div className={cn.list}>
          {notes.map((item, index) => {
            return (
              <NoteCard
                key={item?.id}
                note={item}
                className={[cn.b1, cn.b2, cn.b3][index % 3]}
                onClick={() => onUpdateNote(item)}
                onRemove={() => onRemove(item)}
              />
            )
          })}
          <AddNoteCard onClick={onUpdateNote} />
        </div>
      )}
    </div>
  )
}

export default NotesList
